import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BaseTableTitle,
  BaseTableValue,
  BaseTablesResponse,
  BaseTableValuesResponse,
} from "../../models/BaseTable.model";
import {
  getBaseTables,
  getBaseTableValues,
  createBaseTableValue,
  deleteBaseTableValue,
} from "../thunks/baseTableThunks";

interface BaseTableState {
  // כותרות טבלאות
  tables: BaseTableTitle[];
  tablesLoading: boolean;
  tablesError: string | null;

  // ערכים לטבלה נוכחית
  currentTableCode: number | null;
  currentTableName: string;
  currentTableValues: BaseTableValue[];
  valuesLoading: boolean;
  valuesError: string | null;

  // פעולות CRUD על ערכים
  operationLoading: boolean;
  operationError: string | null;
  operationSuccess: boolean;
  operationMessage: string;

  // כללי
  initialized: boolean;
}

const initialState: BaseTableState = {
  tables: [],
  tablesLoading: false,
  tablesError: null,

  currentTableCode: null,
  currentTableName: "",
  currentTableValues: [],
  valuesLoading: false,
  valuesError: null,

  operationLoading: false,
  operationError: null,
  operationSuccess: false,
  operationMessage: "",

  initialized: false,
};

const baseTableSlice = createSlice({
  name: "baseTables",
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.tablesError = null;
      state.valuesError = null;
      state.operationError = null;
      state.operationMessage = "";
      state.operationSuccess = false;
    },
    resetBaseTableState: (state) => {
      Object.assign(state, initialState);
    },
    clearCurrentTable: (state) => {
      state.currentTableCode = null;
      state.currentTableName = "";
      state.currentTableValues = [];
      state.valuesError = null;
    },
    setCurrentTable: (
      state,
      action: PayloadAction<{ tableCode: number; tableName: string }>
    ) => {
      state.currentTableCode = action.payload.tableCode;
      state.currentTableName = action.payload.tableName;
      state.currentTableValues = [];
      state.valuesError = null;
    },

    clearTablesOnCompanyChange: (state) => {
      state.tables = [];
      state.currentTableCode = null;
      state.currentTableName = "";
      state.currentTableValues = [];
      state.initialized = false;
      state.tablesError = null;
      state.valuesError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Base Tables
      .addCase(getBaseTables.pending, (state) => {
        state.tablesLoading = true;
        state.tablesError = null;
      })
      .addCase(
        getBaseTables.fulfilled,
        (state, action: PayloadAction<BaseTablesResponse>) => {
          state.tablesLoading = false;
          state.tables = action.payload.tables;
          state.initialized = true;
        }
      )
      .addCase(getBaseTables.rejected, (state, action) => {
        state.tablesLoading = false;
        state.tablesError =
          (action.payload as string) || "Failed to fetch base tables";
      })

      // Get Table Values
      .addCase(getBaseTableValues.pending, (state) => {
        state.valuesLoading = true;
        state.valuesError = null;
      })
      .addCase(
        getBaseTableValues.fulfilled,
        (state, action: PayloadAction<BaseTableValuesResponse>) => {
          state.valuesLoading = false;
          state.currentTableCode = action.payload.tableCode;
          state.currentTableName = action.payload.tableName;
          state.currentTableValues = action.payload.values;
        }
      )
      .addCase(getBaseTableValues.rejected, (state, action) => {
        state.valuesLoading = false;
        state.valuesError =
          (action.payload as string) || "Failed to fetch table values";
      })

      // Create/Update Value
      .addCase(createBaseTableValue.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(createBaseTableValue.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.operationMessage = action.payload.message;

        const requestData = action.meta.arg;
        const isUpdate = requestData.valueCode > 0;

        if (isUpdate) {
          // עדכון ערך קיים
          const index = state.currentTableValues.findIndex(
            (v) => v.valueCode === requestData.valueCode
          );
          if (index !== -1) {
            const updatedValue: BaseTableValue = {
              tableCode: requestData.tableCode,
              valueCode: requestData.valueCode,
              valueText: requestData.valueText,
              isDefault: requestData.isDefault,
              valueData1: requestData.valueData1,
              valueData2: requestData.valueData2,
              valueData3: requestData.valueData3,
              displayText: requestData.valueText,
              hasCustomData: !!(
                requestData.valueData1 ||
                requestData.valueData2 ||
                requestData.valueData3
              ),
            };

            // אם זה ברירת מחדל חדשה, הסר מכל השאר
            if (updatedValue.isDefault) {
              state.currentTableValues = state.currentTableValues.map((v) =>
                v.valueCode !== updatedValue.valueCode
                  ? { ...v, isDefault: false }
                  : updatedValue
              );
            } else {
              state.currentTableValues[index] = updatedValue;
            }
          }
        } else {
          // הוספת ערך חדש
          const newValue: BaseTableValue = {
            tableCode: requestData.tableCode,
            valueCode: action.payload.valueCode,
            valueText: requestData.valueText,
            isDefault: requestData.isDefault,
            valueData1: requestData.valueData1,
            valueData2: requestData.valueData2,
            valueData3: requestData.valueData3,
            displayText: requestData.valueText,
            hasCustomData: !!(
              requestData.valueData1 ||
              requestData.valueData2 ||
              requestData.valueData3
            ),
          };

          // אם זה ברירת מחדל, הסר מכל השאר
          if (newValue.isDefault) {
            state.currentTableValues = state.currentTableValues.map((v) => ({
              ...v,
              isDefault: false,
            }));
          }

          state.currentTableValues.push(newValue);
        }
      })
      .addCase(createBaseTableValue.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to create/update value";
      })

      // Delete Value
      .addCase(deleteBaseTableValue.pending, (state) => {
        state.operationLoading = true;
        state.operationError = null;
        state.operationSuccess = false;
      })
      .addCase(deleteBaseTableValue.fulfilled, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = true;
        state.operationMessage = action.payload.message;

        const requestData = action.meta.arg;
        const deletedValueCode = requestData.valueCode;

        state.currentTableValues = state.currentTableValues.filter(
          (v) => v.valueCode !== deletedValueCode
        );
      })
      .addCase(deleteBaseTableValue.rejected, (state, action) => {
        state.operationLoading = false;
        state.operationSuccess = false;
        state.operationError =
          (action.payload as string) || "Failed to delete value";
      });
  },
});

export const {
  clearMessages,
  resetBaseTableState,
  clearCurrentTable,
  setCurrentTable,
  clearTablesOnCompanyChange, // הוסף זה
} = baseTableSlice.actions;

export default baseTableSlice.reducer;

// Selectors
export const selectBaseTables = (state: { baseTables: BaseTableState }) =>
  state.baseTables.tables;
export const selectTablesLoading = (state: { baseTables: BaseTableState }) =>
  state.baseTables.tablesLoading;
export const selectTablesError = (state: { baseTables: BaseTableState }) =>
  state.baseTables.tablesError;
export const selectTablesInitialized = (state: {
  baseTables: BaseTableState;
}) => state.baseTables.initialized;

export const selectCurrentTableCode = (state: { baseTables: BaseTableState }) =>
  state.baseTables.currentTableCode;
export const selectCurrentTableName = (state: { baseTables: BaseTableState }) =>
  state.baseTables.currentTableName;
export const selectCurrentTableValues = (state: {
  baseTables: BaseTableState;
}) => state.baseTables.currentTableValues;
export const selectValuesLoading = (state: { baseTables: BaseTableState }) =>
  state.baseTables.valuesLoading;
export const selectValuesError = (state: { baseTables: BaseTableState }) =>
  state.baseTables.valuesError;

export const selectOperationLoading = (state: { baseTables: BaseTableState }) =>
  state.baseTables.operationLoading;
export const selectOperationError = (state: { baseTables: BaseTableState }) =>
  state.baseTables.operationError;
export const selectOperationSuccess = (state: { baseTables: BaseTableState }) =>
  state.baseTables.operationSuccess;
export const selectOperationMessage = (state: { baseTables: BaseTableState }) =>
  state.baseTables.operationMessage;

// Computed selectors
export const selectCurrentTableHasValues = (state: {
  baseTables: BaseTableState;
}) => state.baseTables.currentTableValues.length > 0;

export const selectTableByCode =
  (tableCode: number) => (state: { baseTables: BaseTableState }) =>
    state.baseTables.tables.find((table) => table.tableCode === tableCode);
